import React from 'react';

interface TypographyProps {
  children: React.ReactNode;
  className?: string;
  as?: keyof JSX.IntrinsicElements;
}

/**
 * Design System Typography Components
 * 
 * Hierarchy:
 * - Display: Hero sections, major headings
 * - Heading: Section titles, card headers
 * - Body: Main content, descriptions
 * - Caption: Metadata, secondary information
 * - Label: Form labels, UI elements
 */

// Display Text - Hero sections
export const Display: React.FC<TypographyProps> = ({ 
  children, 
  className = '', 
  as: Component = 'h1' 
}) => (
  <Component className={`
    text-4xl md:text-5xl lg:text-6xl font-bold leading-tight
    text-text-primary tracking-tight
    ${className}
  `}>
    {children}
  </Component>
);

// Heading 1 - Main page titles
export const Heading1: React.FC<TypographyProps> = ({ 
  children, 
  className = '', 
  as: Component = 'h1' 
}) => (
  <Component className={`
    text-3xl md:text-4xl font-bold leading-tight
    text-text-primary tracking-tight
    ${className}
  `}>
    {children}
  </Component>
);

// Heading 2 - Section titles
export const Heading2: React.FC<TypographyProps> = ({ 
  children, 
  className = '', 
  as: Component = 'h2' 
}) => (
  <Component className={`
    text-2xl md:text-3xl font-semibold leading-tight
    text-text-primary tracking-tight
    ${className}
  `}>
    {children}
  </Component>
);

// Heading 3 - Subsection titles
export const Heading3: React.FC<TypographyProps> = ({ 
  children, 
  className = '', 
  as: Component = 'h3' 
}) => (
  <Component className={`
    text-xl md:text-2xl font-semibold leading-snug
    text-text-primary
    ${className}
  `}>
    {children}
  </Component>
);

// Heading 4 - Card headers, smaller sections
export const Heading4: React.FC<TypographyProps> = ({ 
  children, 
  className = '', 
  as: Component = 'h4' 
}) => (
  <Component className={`
    text-lg md:text-xl font-semibold leading-snug
    text-text-primary
    ${className}
  `}>
    {children}
  </Component>
);

// Body Large - Important content
export const BodyLarge: React.FC<TypographyProps> = ({ 
  children, 
  className = '', 
  as: Component = 'p' 
}) => (
  <Component className={`
    text-lg leading-relaxed
    text-text-secondary
    ${className}
  `}>
    {children}
  </Component>
);

// Body - Main content
export const Body: React.FC<TypographyProps> = ({ 
  children, 
  className = '', 
  as: Component = 'p' 
}) => (
  <Component className={`
    text-base leading-relaxed
    text-text-secondary
    ${className}
  `}>
    {children}
  </Component>
);

// Body Small - Secondary content
export const BodySmall: React.FC<TypographyProps> = ({ 
  children, 
  className = '', 
  as: Component = 'p' 
}) => (
  <Component className={`
    text-sm leading-relaxed
    text-text-tertiary
    ${className}
  `}>
    {children}
  </Component>
);

// Caption - Metadata, timestamps
export const Caption: React.FC<TypographyProps> = ({ 
  children, 
  className = '', 
  as: Component = 'span' 
}) => (
  <Component className={`
    text-xs leading-normal
    text-text-muted font-medium
    ${className}
  `}>
    {children}
  </Component>
);

// Label - Form labels, UI elements
export const Label: React.FC<TypographyProps> = ({ 
  children, 
  className = '', 
  as: Component = 'label' 
}) => (
  <Component className={`
    text-sm font-medium leading-normal
    text-text-primary
    ${className}
  `}>
    {children}
  </Component>
);

// Link - Interactive text elements
interface LinkProps extends TypographyProps {
  href?: string;
  external?: boolean;
}

export const Link: React.FC<LinkProps> = ({ 
  children, 
  className = '', 
  href,
  external = false,
  as: Component = 'a' 
}) => (
  <Component 
    className={`
      text-text-primary font-medium underline underline-offset-2
      hover:text-eerie-black hover:no-underline
      focus:outline-none focus:ring-2 focus:ring-slate-gray/20 focus:rounded
      transition-all duration-200 ease-out
      ${className}
    `}
    href={href}
    {...(external && { target: '_blank', rel: 'noopener noreferrer' })}
  >
    {children}
  </Component>
);